import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from '../../lib/adminApi';
import { ADMIN_SETTINGS } from '../../constants/testIds';

const IntegrationPill = ({ label, connected, testId, extra, action }) => (
  <div data-testid={testId} className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-xl px-4 py-3">
    <div>
      <p className="text-sm font-semibold text-stone-800">{label}</p>
      {extra && <p className="text-xs text-stone-500">{extra}</p>}
    </div>
    <div className="flex items-center gap-3">
      {action}
      {connected ? (
        <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
          <CheckCircle2 size={15} /> Connected
        </span>
      ) : (
        <span className="flex items-center gap-1.5 text-xs font-semibold text-stone-400">
          <XCircle size={15} /> Not connected
        </span>
      )}
    </div>
  </div>
);

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg bg-white border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [saved, setSaved] = useState(false);
  const [relayResult, setRelayResult] = useState(null);
  const [relayLoading, setRelayLoading] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/settings').then(({ data }) => {
      setSettings(data);
      setCompanyName(data.company_name || '');
      setNotificationEmail(data.notification_email || '');
    });
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const { data } = await adminApi.patch('/admin/settings', {
      company_name: companyName,
      notification_email: notificationEmail,
    });
    setSettings((s) => ({ ...s, ...data }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const togglePricing = async () => {
    const { data } = await adminApi.patch('/admin/settings', { pricing_enabled: !settings?.pricing_enabled });
    setSettings((s) => ({ ...s, ...data }));
  };

  const testRelay = async () => {
    setRelayLoading(true);
    setRelayResult(null);
    try {
      const { data } = await adminApi.post('/admin/settings/test-relay');
      setRelayResult({ ok: true, message: `Connected to "${data.business_name}" — ${data.connected_social_accounts} social account(s) linked.` });
    } catch (err) {
      setRelayResult({ ok: false, message: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setRelayLoading(false);
    }
  };

  return (
    <div data-testid={ADMIN_SETTINGS.page} className="max-w-xl">
      <h1 className="text-2xl font-extrabold text-stone-900 mb-1">Settings</h1>
      <p className="text-stone-500 text-sm mb-7">Business details and integration status.</p>

      <div className="bg-white rounded-2xl border border-stone-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-stone-800">Pricing page</h2>
            <p className="text-xs text-stone-500 mt-0.5">
              {settings?.pricing_enabled ? 'Live — visitors can subscribe and pay.' : "Beta mode — checkout buttons show \"Coming soon\", no accidental sign-ups."}
            </p>
          </div>
          <button
            data-testid={ADMIN_SETTINGS.pricingToggle}
            type="button"
            onClick={togglePricing}
            className={`relative w-14 h-8 rounded-full transition ${settings?.pricing_enabled ? 'bg-emerald-500' : 'bg-stone-300'}`}
          >
            <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${settings?.pricing_enabled ? 'translate-x-6' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-6 mb-6 space-y-3">
        <h2 className="text-sm font-bold text-stone-800 mb-1">Integrations</h2>
        <IntegrationPill
          label="Stripe"
          connected={!!settings?.integrations?.stripe?.connected}
          extra={settings?.integrations?.stripe?.mode ? `Mode: ${settings.integrations.stripe.mode}` : undefined}
          testId={ADMIN_SETTINGS.stripeStatus}
        />
        <IntegrationPill label="Local Falcon" connected={!!settings?.integrations?.local_falcon?.connected} testId={ADMIN_SETTINGS.localFalconStatus} />
        <IntegrationPill
          label="Ambanji Relay"
          connected={!!settings?.integrations?.ambanji_relay?.connected}
          extra="CRM automation via your GoHighLevel account"
          testId={ADMIN_SETTINGS.ambanjiRelayStatus}
          action={
            <button
              data-testid={ADMIN_SETTINGS.testRelayButton}
              type="button"
              onClick={testRelay}
              disabled={relayLoading}
              className="text-xs font-semibold px-2.5 py-1 rounded-full border border-stone-300 text-stone-600 hover:bg-white transition disabled:opacity-60"
            >
              {relayLoading ? 'Testing…' : 'Test connection'}
            </button>
          }
        />
      </div>
      {relayResult && (
        <p data-testid={ADMIN_SETTINGS.relayTestResult} className={`text-xs mb-6 -mt-3 ${relayResult.ok ? 'text-emerald-600' : 'text-red-600'}`}>
          {relayResult.message}
        </p>
      )}
      <form onSubmit={handleSave} className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
        <h2 className="text-sm font-bold text-stone-800 mb-1">Business details</h2>
        <div>
          <label className="block text-xs font-semibold text-stone-500 mb-1.5">Company name</label>
          <input data-testid={ADMIN_SETTINGS.companyNameInput} value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-500 mb-1.5">Notification email</label>
          <input data-testid={ADMIN_SETTINGS.notificationEmailInput} type="email" value={notificationEmail} onChange={(e) => setNotificationEmail(e.target.value)} className={inputClass} />
        </div>
        <button data-testid={ADMIN_SETTINGS.saveButton} type="submit" className="px-5 py-2.5 rounded-lg bg-[#F97316] text-white text-sm font-bold hover:bg-[#EA580C] transition">
          {saved ? 'Saved ✓' : 'Save changes'}
        </button>
      </form>
    </div>
  );
};

export default AdminSettings;
